import React, { useEffect, useState, useMemo } from 'react';
import { ApolloProvider } from '@apollo/client';
import './App.css';
import AppOktaRouter from './routes/AppWithRouter';
import { FullstoryWrapper } from './components/FullStory/Fullstory';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { oktaLogin } from './utils/okta_auth';
import { createApolloClient } from './utils/apolloUtils';
import { createMfeApolloClient } from 'utils/apolloUtilsMfe';
import ThemeContextProvider from 'context/ThemeContext';
import { Provider } from 'react-redux';
import store from './store/store';
import { setIsMFE, setMfeHostConfig } from 'store/slices/appSlice';
import {
  ISSUER,
  CLIENT_ID,
  REDIRECT_URI,
  LOGOUT_URI,
  PKCE,
  FULLSTORY_ORG,
} from '@config/envConfig';

import { ThemeProvider as PeacockThemeProvider } from 'pepsico-ds';

// Standalone Application Renderer (exact tree as user requested)
const StandaloneAppRenderer = () => {
  const oktaAuth = useMemo(
    () =>
      new OktaAuth({
        issuer: ISSUER,
        clientId: CLIENT_ID,
        redirectUri: REDIRECT_URI,
        postLogoutRedirectUri: LOGOUT_URI,
        scopes: [
          'openid',
          'profile',
          'email',
          'offline_access',
          'pepapppsdashboard',
        ],
        pkce: PKCE,
      }),
    []
  );

  const triggerLogin = async () => oktaLogin(oktaAuth);

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(
      toRelativeUrl(originalUri || '/', window.location.origin)
    );
  };

  const customAuthHandler = async () => {
    // Skip auto-login on logout page to allow proper logout flow
    if (window.location.pathname === '/logout') {
      return;
    }
    const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
    if (!previousAuthState || !previousAuthState.isAuthenticated) {
      await triggerLogin();
    }
  };

  return (
    <Provider store={store}>
      <PeacockThemeProvider>
        <Security
          oktaAuth={oktaAuth}
          onAuthRequired={customAuthHandler}
          restoreOriginalUri={restoreOriginalUri}
        >
          <ApolloProvider client={createApolloClient(oktaAuth)}>
            <ThemeContextProvider>
              <FullstoryWrapper organization={FULLSTORY_ORG}>
                <AppOktaRouter />
              </FullstoryWrapper>
            </ThemeContextProvider>
          </ApolloProvider>
        </Security>
      </PeacockThemeProvider>
    </Provider>
  );
};

// MFE Application Renderer (explicit, non-reused tree)
const MfeAppRenderer = ({ config }) => {
  const [accessToken, setAccessToken] = useState(config?.accessToken || null);

  useEffect(() => {
    if (!config || !config.isMFE) {
      return;
    }
    const handleAccessTokenChange = (event) => {
      setAccessToken(event.detail);
    };
    window.addEventListener('accessTokenChanged', handleAccessTokenChange);
    return () =>
      window.removeEventListener('accessTokenChanged', handleAccessTokenChange);
  }, [config]);

  if (!accessToken && config?.isMFE) {
    return <div>Loading MFE data...</div>;
  }

  return (
    <Provider store={store}>
      <PeacockThemeProvider
        defaultTheme={
          config?.themeMode === 'dark' ? 'Dark Peacock' : 'Light Peacock'
        }
      >
        <ApolloProvider
          client={createMfeApolloClient({
            uri: config.uri || '',
            initialToken: accessToken,
            refreshTokenFunc: config.refreshToken,
            accessTokenStorageKey: config.accessTokenStorageKey,
            mfeType: config.type,
          })}
        >
          <ThemeContextProvider>
            <div className="ps-dashboard-root">
              <AppOktaRouter />
            </div>
          </ThemeContextProvider>
        </ApolloProvider>
      </PeacockThemeProvider>
    </Provider>
  );
};

function App({ config }) {
  const effectiveIsMFE = config?.isMFE || config?.accessToken != null;

  useMemo(() => {
    if (effectiveIsMFE) {
      store.dispatch(setIsMFE(true));
      if (config) {
        store.dispatch(
          setMfeHostConfig({
            type: config.type,
            themeMode: config.themeMode,
            parentElementId: config.parentElementId,
            redirectUrl: config.redirectUrl,
          })
        );
      }
    } else {
      store.dispatch(setIsMFE(false));
      store.dispatch(setMfeHostConfig(null));
    }
  }, [effectiveIsMFE, config]);

  return effectiveIsMFE ? (
    <MfeAppRenderer config={config} />
  ) : (
    <StandaloneAppRenderer />
  );
}

export default App;
